import React, { useState } from 'react';
import {
  FiCode,
  FiKey,
  FiCreditCard,
  FiCalendar,
  FiHeart,
  FiGift,
  FiDollarSign,
  FiChevronDown,
  FiChevronRight,
  FiCopy,
  FiCheck,
  FiExternalLink,
  FiZap,
  FiShield,
  FiGlobe,
  FiBook,
  FiShoppingBag,
  FiPackage,
  FiHome,
  FiUsers,
  FiSmartphone,
} from 'react-icons/fi';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

interface Endpoint {
  method: HttpMethod;
  path: string;
  description: string;
  auth?: boolean;
}

interface ApiSection {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  endpoints: Endpoint[];
}

const methodColors: Record<HttpMethod, string> = {
  GET: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  POST: 'bg-blue-100 text-blue-700 border-blue-200',
  PUT: 'bg-amber-100 text-amber-700 border-amber-200',
  DELETE: 'bg-red-100 text-red-700 border-red-200',
  PATCH: 'bg-purple-100 text-purple-700 border-purple-200',
};

const apiSections: ApiSection[] = [
  {
    id: 'auth',
    title: 'Autenticação',
    description: 'Endpoints para autenticação e gestão de tokens.',
    icon: <FiKey className="w-5 h-5" />,
    endpoints: [
      { method: 'POST', path: '/v1/auth/register', description: 'Registar novo utilizador' },
      { method: 'POST', path: '/v1/auth/login', description: 'Autenticar e obter token' },
      { method: 'POST', path: '/v1/auth/refresh', description: 'Renovar token de acesso', auth: true },
      { method: 'POST', path: '/v1/auth/forgot-password', description: 'Recuperação de senha' },
    ],
  },
  {
    id: 'payments',
    title: 'Pagamentos & eCommerce',
    description: 'Processamento de pagamentos e transações.',
    icon: <FiCreditCard className="w-5 h-5" />,
    endpoints: [
      { method: 'POST', path: '/v1/payments/create', description: 'Criar intenção de pagamento', auth: true },
      { method: 'GET', path: '/v1/payments/:paymentId', description: 'Obter detalhes do pagamento', auth: true },
      { method: 'POST', path: '/v1/payments/:paymentId/refund', description: 'Processar reembolso', auth: true },
      { method: 'GET', path: '/v1/payments/transactions', description: 'Histórico de transações', auth: true },
      { method: 'POST', path: '/v1/payments/webhook', description: 'Configurar webhook', auth: true },
    ],
  },
  {
    id: 'products',
    title: 'Produtos',
    description: 'Gestão de produtos e catálogo.',
    icon: <FiPackage className="w-5 h-5" />,
    endpoints: [
      { method: 'GET', path: '/v1/shop/products', description: 'Listar produtos', auth: true },
      { method: 'GET', path: '/v1/shop/products/:id', description: 'Detalhes do produto', auth: true },
      { method: 'POST', path: '/v1/shop/products', description: 'Criar produto', auth: true },
      { method: 'PUT', path: '/v1/shop/products/:id', description: 'Atualizar produto', auth: true },
      { method: 'DELETE', path: '/v1/shop/products/:id', description: 'Eliminar produto', auth: true },
      { method: 'GET', path: '/v1/shop/categories', description: 'Listar categorias', auth: true },
    ],
  },
  {
    id: 'orders',
    title: 'Encomendas',
    description: 'Gestão de encomendas e vendas.',
    icon: <FiShoppingBag className="w-5 h-5" />,
    endpoints: [
      { method: 'GET', path: '/v1/orders', description: 'Listar encomendas', auth: true },
      { method: 'GET', path: '/v1/orders/:id', description: 'Detalhes da encomenda', auth: true },
      { method: 'GET', path: '/v1/orders/stats', description: 'Estatísticas de encomendas', auth: true },
      { method: 'PUT', path: '/v1/orders/:id/status', description: 'Atualizar status', auth: true },
      { method: 'POST', path: '/v1/orders/:id/refund', description: 'Processar devolução', auth: true },
    ],
  },
  {
    id: 'inventory',
    title: 'Inventário',
    description: 'Gestão de stock e movimentos.',
    icon: <FiPackage className="w-5 h-5" />,
    endpoints: [
      { method: 'GET', path: '/v1/inventory/overview', description: 'Resumo do inventário', auth: true },
      { method: 'GET', path: '/v1/inventory/products', description: 'Produtos com stock', auth: true },
      { method: 'PUT', path: '/v1/inventory/products/:id', description: 'Atualizar stock', auth: true },
      { method: 'GET', path: '/v1/inventory/movements', description: 'Movimentos de stock', auth: true },
      { method: 'POST', path: '/v1/inventory/movements', description: 'Registar movimento', auth: true },
      { method: 'GET', path: '/v1/inventory/alerts', description: 'Alertas de stock', auth: true },
    ],
  },
  {
    id: 'stores',
    title: 'Lojas',
    description: 'Gestão de lojas e comerciantes.',
    icon: <FiHome className="w-5 h-5" />,
    endpoints: [
      { method: 'GET', path: '/v1/shop/stores', description: 'Listar lojas', auth: true },
      { method: 'GET', path: '/v1/shop/stores/:id', description: 'Detalhes da loja', auth: true },
      { method: 'POST', path: '/v1/shop/stores', description: 'Criar loja', auth: true },
      { method: 'PUT', path: '/v1/shop/stores/:id', description: 'Atualizar loja', auth: true },
      { method: 'GET', path: '/v1/shop/store-types', description: 'Tipos de lojas', auth: true },
    ],
  },
  {
    id: 'customers',
    title: 'Clientes',
    description: 'Gestão de clientes.',
    icon: <FiUsers className="w-5 h-5" />,
    endpoints: [
      { method: 'GET', path: '/v1/customers', description: 'Listar clientes', auth: true },
      { method: 'GET', path: '/v1/customers/:id', description: 'Detalhes do cliente', auth: true },
      { method: 'POST', path: '/v1/customers', description: 'Criar cliente', auth: true },
      { method: 'GET', path: '/v1/customers/:id/transactions', description: 'Histórico do cliente', auth: true },
    ],
  },
  {
    id: 'pos',
    title: 'POS',
    description: 'API para terminais de ponto de venda.',
    icon: <FiSmartphone className="w-5 h-5" />,
    endpoints: [
      { method: 'POST', path: '/v1/pos/auth/login', description: 'Login do terminal POS' },
      { method: 'POST', path: '/v1/pos/payment-request', description: 'Criar pedido de pagamento', auth: true },
      { method: 'GET', path: '/v1/pos/payment-request/:id/status', description: 'Status do pagamento', auth: true },
      { method: 'POST', path: '/v1/pos/pay', description: 'Processar pagamento', auth: true },
      { method: 'GET', path: '/v1/pos/transactions', description: 'Transações POS', auth: true },
      { method: 'GET', path: '/v1/pos/dashboard', description: 'Dashboard POS', auth: true },
    ],
  },
  {
    id: 'events',
    title: 'Eventos',
    description: 'Gestão de eventos e bilhetes.',
    icon: <FiCalendar className="w-5 h-5" />,
    endpoints: [
      { method: 'GET', path: '/v1/public/events', description: 'Listar eventos públicos' },
      { method: 'GET', path: '/v1/public/events/:eventId', description: 'Detalhes do evento' },
      { method: 'POST', path: '/v1/events/tickets/purchase', description: 'Comprar bilhetes', auth: true },
      { method: 'POST', path: '/v1/events/tickets/:ticketId/validate', description: 'Validar bilhete', auth: true },
      { method: 'POST', path: '/v1/events/tickets/:ticketId/checkin', description: 'Check-in', auth: true },
    ],
  },
  {
    id: 'donations',
    title: 'Doações',
    description: 'Campanhas e gestão de doações.',
    icon: <FiHeart className="w-5 h-5" />,
    endpoints: [
      { method: 'GET', path: '/v1/public/donations/campaigns', description: 'Campanhas ativas' },
      { method: 'GET', path: '/v1/public/donations/campaigns/:campaignId', description: 'Detalhes da campanha' },
      { method: 'POST', path: '/v1/donations/donate', description: 'Fazer doação' },
      { method: 'GET', path: '/v1/donations/:donationId/receipt', description: 'Recibo de doação' },
    ],
  },
  {
    id: 'vouchers',
    title: 'Vouchers',
    description: 'Vouchers e códigos promocionais.',
    icon: <FiGift className="w-5 h-5" />,
    endpoints: [
      { method: 'POST', path: '/v1/vouchers/create', description: 'Criar voucher', auth: true },
      { method: 'GET', path: '/v1/vouchers/:voucherId', description: 'Detalhes do voucher', auth: true },
      { method: 'POST', path: '/v1/vouchers/validate', description: 'Validar código', auth: true },
      { method: 'POST', path: '/v1/vouchers/:voucherId/redeem', description: 'Resgatar voucher', auth: true },
      { method: 'GET', path: '/v1/vouchers', description: 'Listar vouchers', auth: true },
    ],
  },
  {
    id: 'wallet',
    title: 'Wallet',
    description: 'Carteira digital e transferências.',
    icon: <FiDollarSign className="w-5 h-5" />,
    endpoints: [
      { method: 'GET', path: '/v1/wallet/balance', description: 'Saldo da carteira', auth: true },
      { method: 'POST', path: '/v1/wallet/topup', description: 'Carregar saldo', auth: true },
      { method: 'POST', path: '/v1/wallet/transfer', description: 'Transferir saldo', auth: true },
      { method: 'GET', path: '/v1/wallet/transactions', description: 'Histórico', auth: true },
      { method: 'POST', path: '/v1/wallet/pin/set', description: 'Definir PIN', auth: true },
    ],
  },
];

const CodeBlock: React.FC<{ code: string }> = ({ code }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group">
      <pre className="bg-gray-900 rounded-lg p-4 overflow-x-auto text-sm font-mono text-gray-300">
        <code>{code}</code>
      </pre>
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 p-2 rounded-md bg-gray-800 hover:bg-gray-700
                   text-gray-400 hover:text-gray-200 opacity-0 group-hover:opacity-100 transition-all"
      >
        {copied ? <FiCheck className="w-4 h-4" /> : <FiCopy className="w-4 h-4" />}
      </button>
    </div>
  );
};

const ApiSectionComponent: React.FC<{ section: ApiSection }> = ({ section }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div id={section.id} className="scroll-mt-24">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow"
      >
        <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center text-indigo-600">
          {section.icon}
        </div>
        <div className="text-left flex-1">
          <h2 className="text-lg font-semibold text-gray-900">{section.title}</h2>
          <p className="text-sm text-gray-500">{section.description}</p>
        </div>
        <span className="text-sm text-gray-400">{section.endpoints.length} endpoints</span>
        {isExpanded ? (
          <FiChevronDown className="w-5 h-5 text-gray-400" />
        ) : (
          <FiChevronRight className="w-5 h-5 text-gray-400" />
        )}
      </button>

      {isExpanded && (
        <div className="mt-2 space-y-2 ml-14">
          {section.endpoints.map((endpoint, index) => (
            <div
              key={index}
              className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
            >
              <span
                className={`px-2 py-1 text-xs font-bold rounded border ${methodColors[endpoint.method]}`}
              >
                {endpoint.method}
              </span>
              <code className="text-sm font-mono text-gray-700 flex-1">
                {endpoint.path}
              </code>
              {endpoint.auth && (
                <span className="px-2 py-1 text-xs bg-amber-100 text-amber-700 rounded">
                  Auth
                </span>
              )}
              <span className="text-sm text-gray-500">{endpoint.description}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Developers: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center">
                <FiCode className="w-6 h-6 text-white" />
              </div>
              <span className="px-4 py-1.5 rounded-full bg-white/10 text-white/90 text-sm font-medium">
                API v1.0
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Pagali API
            </h1>
            <p className="text-lg text-white/80 max-w-3xl mx-auto mb-10">
              Integra os serviços Pagali na tua aplicação. Pagamentos, eCommerce, eventos, doações,
              vouchers, POS e muito mais.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <a
                href="#auth"
                className="px-6 py-3 bg-white text-indigo-700 font-semibold rounded-xl hover:bg-white/90 transition-colors flex items-center gap-2"
              >
                <FiBook className="w-5 h-5" />
                Começar
              </a>
              <a
                href="/developers/api-keys"
                className="px-6 py-3 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors flex items-center gap-2"
              >
                <FiKey className="w-5 h-5" />
                Obter API Keys
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: <FiZap />, title: 'Rápida & Fiável', description: '99.9% uptime garantido' },
            { icon: <FiShield />, title: 'Segura', description: 'Encriptação TLS e JWT' },
            { icon: <FiGlobe />, title: 'Sandbox', description: 'Ambiente de testes completo' },
          ].map((feature, index) => (
            <div key={index} className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center text-indigo-600 mb-4">
                {feature.icon}
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
              <p className="text-gray-500">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Base URL */}
        <div className="bg-gray-100 rounded-2xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Base URL</h3>
          <div className="space-y-3">
            <div>
              <span className="text-sm text-gray-500">Produção:</span>
              <CodeBlock code="https://api.pagali.cv" />
            </div>
            <div>
              <span className="text-sm text-gray-500">Sandbox:</span>
              <CodeBlock code="https://sandbox-api.pagali.cv" />
            </div>
          </div>
        </div>

        {/* Authentication */}
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-amber-900 mb-2">Autenticação</h3>
          <p className="text-amber-800 mb-4">
            A maioria dos endpoints requer autenticação via Bearer token:
          </p>
          <CodeBlock
            code={`Authorization: Bearer <your_api_token>

curl -X GET "https://api.pagali.cv/v1/wallet/balance" \\
  -H "Authorization: Bearer eyJhbGciOiJIUzI1NiIs..." \\
  -H "Content-Type: application/json"`}
          />
        </div>

        {/* API Sections */}
        <div className="space-y-4">
          {apiSections.map((section) => (
            <ApiSectionComponent key={section.id} section={section} />
          ))}
        </div>

        {/* Error Handling */}
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mt-8">
          <h3 className="text-lg font-semibold text-red-900 mb-4">Tratamento de Erros</h3>
          <CodeBlock
            code={`{
  "success": false,
  "error": {
    "code": "INVALID_REQUEST",
    "message": "O campo 'amount' é obrigatório"
  }
}

// Códigos: 400, 401, 403, 404, 429, 500`}
          />
        </div>

        {/* Support */}
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-2xl p-6 mt-8">
          <h3 className="text-lg font-semibold text-indigo-900 mb-2">Precisa de ajuda?</h3>
          <p className="text-indigo-800 mb-4">
            A nossa equipa de suporte técnico está disponível para ajudar.
          </p>
          <div className="flex flex-wrap gap-4">
            <a
              href="mailto:developers@pagali.cv"
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              developers@pagali.cv
            </a>
            <a
              href="https://pagali.cv/support"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-white text-indigo-600 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              Centro de Suporte
              <FiExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Developers;
